// =====================================================================
// ExpenseFormDialog (Financeiro F2 / S58 · S94 paga+comprovante) — cadastra
// uma conta a pagar / despesa. [S94] Opcao de lancar JA PAGA (data + forma de
// pagamento) + anexar comprovante de pagamento (foto/PDF, sobe apos salvar).
// =====================================================================
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress
} from "@material-ui/core";
import { AttachFile } from "@material-ui/icons";
import { toast } from "react-toastify";
import { createExpense, uploadExpenseReceipt, financeErrorMessage } from "../../services/financeService";
import useIsMobile from "../../utils/useIsMobile";

// Formas de pagamento (casa com EXPENSE_METHODS do backend). Inline p/ evitar
// dependencia de export externo (nao quebra o build se o export mudar de lugar).
const PAYMENT_METHODS = [
  { value: "dinheiro", label: "Dinheiro" },
  { value: "pix", label: "PIX" },
  { value: "debito", label: "Cartao de debito" },
  { value: "credito", label: "Cartao de credito" },
  { value: "transferencia", label: "Transferencia" },
  { value: "boleto", label: "Boleto" }
];

const todayStr = () => new Date().toISOString().slice(0, 10);

const ExpenseFormDialog = ({ open, unitId, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [supplier, setSupplier] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [amount, setAmount] = useState("");
  // [S94]
  const [isPaid, setIsPaid] = useState(false);
  const [paidAt, setPaidAt] = useState(todayStr());
  const [paymentMethod, setPaymentMethod] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDescription("");
      setCategory("");
      setSupplier("");
      setDueDate("");
      setAmount("");
      setIsPaid(false);
      setPaidAt(todayStr());
      setPaymentMethod("");
      setReceiptFile(null);
    }
  }, [open]);

  const handleSave = async () => {
    const value = Number(String(amount).replace(",", "."));
    if (!value || value <= 0) {
      toast.error("Informe um valor valido.");
      return;
    }
    if (!isPaid && !dueDate) {
      toast.error("Informe o vencimento.");
      return;
    }
    if (isPaid && !paymentMethod) {
      toast.error("Selecione a forma de pagamento.");
      return;
    }
    setSaving(true);
    try {
      const created = await createExpense({
        description,
        category,
        supplier,
        dueDate: dueDate || undefined,
        amount: value,
        // [S90] unidade ativa (modelo B); ignorado se a company nao tem unidades.
        unitId: unitId != null ? unitId : undefined,
        // [S94] lancar ja paga
        markPaid: isPaid || undefined,
        paidAt: isPaid ? paidAt || undefined : undefined,
        paymentMethod: isPaid ? paymentMethod : undefined
      });
      // [S94] anexo (opcional) — sobe DEPOIS de criar, como no "Registrar recebimento".
      if (receiptFile && created && created.id) {
        try {
          await uploadExpenseReceipt(created.id, receiptFile);
        } catch (upErr) {
          toast.warn("Despesa salva, mas o comprovante falhou. Anexe de novo pela despesa.");
        }
      }
      toast.success(isPaid ? "Despesa lancada (paga)." : "Despesa cadastrada.");
      onSaved && onSaved();
    } catch (err) {
      toast.error(financeErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle>Nova despesa</DialogTitle>
      <DialogContent>
        <TextField
          label="Descricao"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Categoria"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          fullWidth
          margin="dense"
          placeholder="ex.: Aluguel, Insumos, Salarios"
        />
        <TextField
          label="Fornecedor"
          value={supplier}
          onChange={(e) => setSupplier(e.target.value)}
          fullWidth
          margin="dense"
        />
        <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }}>
          <TextField
            label={isPaid ? "Vencimento (opcional)" : "Vencimento"}
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            margin="dense"
            style={{ flexGrow: 1, minWidth: 150 }}
          />
          <TextField
            label="Valor (R$)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ step: "0.01", min: "0" }}
            margin="dense"
            style={{ flexGrow: 1, minWidth: 130 }}
          />
        </Box>

        {/* [S94] Lancar ja paga */}
        <FormControlLabel
          control={
            <Checkbox
              checked={isPaid}
              onChange={(e) => setIsPaid(e.target.checked)}
              color="primary"
            />
          }
          label="Lancar como paga (ja quitada)"
          style={{ marginTop: 4 }}
        />
        {isPaid && (
          <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }}>
            <TextField
              label="Data do pagamento"
              type="date"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              margin="dense"
              style={{ flexGrow: 1, minWidth: 150 }}
            />
            <FormControl margin="dense" style={{ flexGrow: 1, minWidth: 160 }}>
              <InputLabel shrink>Forma de pagamento</InputLabel>
              <Select
                value={paymentMethod}
                displayEmpty
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <MenuItem value="">
                  <em>Selecione...</em>
                </MenuItem>
                {PAYMENT_METHODS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* [S94] Comprovante de pagamento (foto/PDF) */}
        <Box style={{ marginTop: 12 }}>
          <input
            id="expense-receipt-input"
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf"
            style={{ display: "none" }}
            onChange={(e) =>
              setReceiptFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)
            }
          />
          <label htmlFor="expense-receipt-input">
            <Button component="span" variant="outlined" size="small" startIcon={<AttachFile />}>
              {receiptFile ? "Trocar comprovante" : "Anexar comprovante"}
            </Button>
          </label>
          {receiptFile && (
            <Typography variant="caption" component="span" style={{ marginLeft: 8 }}>
              {receiptFile.name}
              <Button
                size="small"
                onClick={() => setReceiptFile(null)}
                style={{ minWidth: 0, marginLeft: 4, padding: "0 6px" }}
              >
                x
              </Button>
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseFormDialog;
